import { useEffect, useState, type ReactNode } from "react";
import { AdminAuthContext } from "@src/presentation/hooks/auth/useAdminAuthContext";
import { useAdminAuth, useAuth } from "../hooks/auth";
import type { AdminUser } from "@src/domain/entities";
import { LoadingSpinner } from "@src/presentation/components/common";
import { Division } from "@ir/ui";
import { AdminAuthProviderError } from "@src/presentation/providers/AdminAuthProvider.error";
import { GlobalDataLookupProvider } from "./GlobalDataLookupProvider";

/**
 * Admin Auth Provider - Presentation layer
 * Single Responsibility: Wraps app with AdminAuth service provider
 * Must be used inside AuthProvider
 */
interface AdminAuthProviderProps {
  children: ReactNode;
}

export const AdminAuthProvider = ({ children }: AdminAuthProviderProps) => {
  const auth = useAuth();

  const adminAuthService = useAdminAuth();
  const [loading, setLoading] = useState<boolean>(true);
  const [user, setUser] = useState<AdminUser>();
  const [error, setError] = useState<string | null>(null);

  useEffect(() => {
    const initializeAuth = async () => {
      try {
        setLoading(true);
        setError(null);

        // Check if user exists, if not, login
        const currentUser =
          adminAuthService.getUser() ??
          (await adminAuthService.login()) ??
          null;

        setUser(currentUser ?? undefined);
      } catch (err) {
        console.error("Admin auth initialization error:", err);
        setError(
          err instanceof Error
            ? err.message
            : "Failed to initialize admin authentication",
        );
        setUser(undefined);
      } finally {
        setLoading(false);
      }
    };

    initializeAuth();
  }, [adminAuthService, auth]);

  if (loading || adminAuthService.isLoading() || auth.isLoading()) {
    return <LoadingSpinner />;
  }

  if (error) {
    return <AdminAuthProviderError />;
  }

  if (!user) {
    return <Division>Invalid Admin User</Division>;
  }

  return (
    <AdminAuthContext.Provider value={adminAuthService}>
      <GlobalDataLookupProvider>{children}</GlobalDataLookupProvider>
    </AdminAuthContext.Provider>
  );
};
